import { Button } from "@features/atoms/Button"
import { Spinner } from "@features/motion/Spinner"
import { useRPCQuery } from "eddev/hooks"
import { RefObject, useEffect, useRef, useState } from "react"
import { useScript } from "usehooks-ts"
import { CheckoutStepLayout } from "./CheckoutStepLayout"
import { useCheckout } from "./useCheckout"
import { CartPanel } from "./CartPanel"
import { getSpektrixCheckoutReturn } from "./spektrix-checkout"

export function PaymentCheckoutStep() {
  const checkout = useCheckout()
  const config = useRPCQuery("api.ticketing.checkoutConfig", undefined, {
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })
  const [checkoutVersion, setCheckoutVersion] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <CheckoutStepLayout
      padded={false}
      contentRef={contentRef}
      children={
        config.isPending ? (
          <p className="type-body-xs p-4" role="status">
            Loading secure checkout…
          </p>
        ) : config.isError ? (
          <div className="type-body-xs p-4" role="alert">
            <p>Secure checkout could not be loaded.</p>
            <Button variant="outline" onClick={() => void config.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <SpektrixCheckoutFrame
            key={`${config.data.webApiBaseUrl}:${checkoutVersion}`}
            webApiBaseUrl={config.data.webApiBaseUrl}
            scrollContainer={contentRef}
          />
        )
      }
      // sidePanel={<CartPanel onTicketsRemoved={() => setCheckoutVersion((version) => version + 1)} />}
    />
  )
}

function SpektrixCheckoutFrame({
  webApiBaseUrl,
  scrollContainer,
}: {
  webApiBaseUrl: string
  scrollContainer: RefObject<HTMLDivElement>
}) {
  const [checkoutUrl] = useState(() => {
    const checkoutReturn =
      typeof window === "undefined" ? null : getSpektrixCheckoutReturn(webApiBaseUrl, window.location.search)
    // TODO: Drop the stylesheet override once spektrix.css is the domain's default stylesheet.
    return (
      checkoutReturn ??
      // Let Spektrix initialise checkout and select the steps required by this basket.
      new URL("../website/secure/Checkout.aspx?resize=true", webApiBaseUrl).href
    )
  })
  const integrationScriptUrl = new URL("../website/scripts/integrate.js", webApiBaseUrl).href
  const checkout = useCheckout()
  const iframe = useRef<HTMLIFrameElement>(null)
  // integrate.js installs global listeners, so retain and reuse it across dialog mounts.
  const scriptStatus = useScript(integrationScriptUrl, { removeOnUnmount: false })
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  const [refreshVersion, setRefreshVersion] = useState(0)
  const refreshedVersion = useRef(0)

  useEffect(() => {
    if (!loaded || getSpektrixCheckoutReturn(webApiBaseUrl, window.location.search) !== checkoutUrl) return
    const url = new URL(window.location.href)
    url.searchParams.delete("SpektrixPasswordlessToken")
    url.searchParams.delete("SpektrixRedirectUrl")
    window.history.replaceState(window.history.state, "", url)
  }, [loaded, checkoutUrl, webApiBaseUrl])

  useEffect(() => {
    const origin = new URL(checkoutUrl).origin
    function onMessage(event: MessageEvent) {
      if (event.origin !== origin || event.source !== iframe.current?.contentWindow || typeof event.data !== "string") {
        return
      }
      if (event.data.startsWith("basketHash:")) {
        // A page loaded or the basket changed; this is not proof of payment.
        setLoaded(true)
        setRefreshVersion((version) => version + 1)
        return
      }
      try {
        const offset: unknown = JSON.parse(event.data)?.spektrixOffset
        if (typeof offset === "number" && Number.isFinite(offset) && offset >= 0) {
          // Express Checkout scrolls within our dialog, rather than the background page.
          scrollContainer.current?.scrollTo({ top: offset })
        }
      } catch {
        // Other Spektrix messages (including resize) are handled by integrate.js.
      }
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [checkoutUrl, scrollContainer])

  useEffect(() => {
    if (checkout.isPending || refreshVersion === refreshedVersion.current) return
    refreshedVersion.current = refreshVersion
    void checkout.refreshBasket()
  }, [checkout.isPending, checkout.refreshBasket, refreshVersion])

  if (scriptStatus === "error" || failed) {
    return (
      <p className="type-body-xs py-4" role="alert">
        Secure checkout could not be loaded. Please reload to try again.
      </p>
    )
  }

  return (
    <div>
      {!loaded && (
        <p className="type-body-xs flex items-center gap-2 p-4" role="status">
          <Spinner aria-hidden="true" /> Loading secure checkout…
        </p>
      )}
      {scriptStatus === "ready" && (
        <iframe
          ref={iframe}
          id="SpektrixIFrame"
          name="SpektrixIFrame"
          title="Secure checkout and payment"
          src={checkoutUrl}
          allow="payment"
          className="block w-full border-0"
          style={{ height: 1000 }}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  )
}
